/**
 * The vertical operational-area sidebar, and the narrow-viewport drawer that
 * carries the same nav.
 *
 * **Which design-system component, and why.** The sidebar is the design
 * system's `NavigationMenu` with `orientation="vertical"` on the root. That is a
 * real prop, not an invention: `NavigationMenuRootProps` is
 * `BaseNavigationMenu.Root.Props & { className }`, and Base UI declares
 * `orientation?: 'horizontal' | 'vertical'` there — the list renders a
 * `CompositeRoot` that takes its arrow-key axis from it, so Up/Down walks the
 * areas. `NavigationMenu.Link` is a composite item rendering an `<a>`, and it
 * sets `aria-current="page"` itself whenever `active` is true, so current-page
 * indication comes from the design system rather than from us. The root renders
 * a `<nav>`, which is the landmark.
 *
 * The one thing the design system does *not* give us is vertical *styling*: its
 * NavigationMenu CSS is `display:flex; align-items:center` with no
 * `[data-orientation]` rules, so the column layout comes from Tailwind
 * utilities passed through the documented `className` prop. Utilities outrank
 * the `norican` cascade layer by design (see `styles/globals.css`), so this is
 * the sanctioned way to re-lay-out a design-system component without forking
 * its CSS.
 *
 * `TreeView` was the other candidate and was rejected: it is a WAI-ARIA `tree`
 * driven by `onSelectedChange`, so its rows are `treeitem`s rather than
 * anchors. A page nav has to be real links — middle-click, open-in-new-tab,
 * copy-link and `aria-current` all depend on it — and `role="tree"` is not a
 * landmark.
 *
 * **The area row is the disclosure.** Clicking an area both navigates to its
 * route and expands its sub-items; the area you are in is the one that is open.
 * For areas that have a page, that needs no state at all — the click navigates,
 * the route changes, and `isCurrentArea` makes the group open — which also makes
 * expansion exclusive for free, since only one area can be current. Areas with
 * no page of their own cannot navigate, so those toggle through
 * `expandedUnbuilt` in the nav root.
 *
 * A consequence worth knowing: you collapse a group by opening another, not by
 * clicking the open one again. Re-clicking the current area would have to either
 * collapse it (a link that hides its own children) or do nothing, and doing
 * nothing is the quieter of the two.
 *
 * The panel uses `hiddenUntilFound`, so a collapsed group still answers the
 * browser's find-in-page and expands itself around the hit — which matters most
 * for the unavailable areas, whose sub-items are the only door to a shipped
 * board.
 *
 * **Status slots are empty on purpose.** Each area row has a slot for a live
 * verdict (`Freshness` — a toned dot). Nothing populates it: every per-area
 * verdict lives in a `features/*` hook, and this file must not import from
 * there. A nav that painted a confident green dot from no data would be worse
 * than a nav with no dots, so the slot stays unfilled until a shell-level
 * aggregate exists to fill it.
 *
 * **Three levels, one model.** An area's sub-items may themselves carry
 * sub-items — the six Dynamics screens hang under the Dynamics board rather than
 * beside M365 — and the nested group opens on exactly the same rule as an area:
 * the row navigates, and being under it is what "open" means, so no extra state
 * exists. This is what replaced the Dynamics feature's own route switcher: a
 * second nav inside a feature drifts from this one and gives the reader two
 * places to look for the same screens.
 *
 * **The two gates render differently.** `adminOnly` rows are filtered out
 * (`AdminRoute` would bounce the reader); `investigatorOnly` rows are shown
 * disabled with one sentence, because their pages render for anyone and then
 * refuse every payload with a 403. Whether *this* reader is an investigator is
 * asked of the API (`useFoInvestigatorAccess`), never derived from a list of
 * addresses held here — and the queue length beside the Reports row comes from
 * the same answer, so the nav never invents a figure. The rules live in
 * `nav-access.ts`; only the rendering lives here.
 */

import { ChevronDown, Clock, Icon, PanelLeft } from "@norican/icons";
import {
  Badge,
  Button,
  cn,
  Collapsible,
  Drawer,
  Freshness,
  type FreshnessTone,
  NavigationMenu,
  ScrollArea,
  Separator,
  Typography,
} from "@norican/react";
import { Fragment, useState } from "react";
import { Link, useLocation } from "react-router";

import { boardVisibility, INVESTIGATOR_ONLY_LINE, type NavViewer } from "@/components/nav-access";
import {
  areaForPath,
  areasInGroup,
  boardsForArea,
  childBoards,
  isAreaAvailable,
  NAV_AREA_GROUP_LABEL,
  NAV_AREA_GROUPS,
  type NavArea,
  type NavAreaGroup,
  navPathForPath,
  type NavSection,
} from "@/components/nav-sections";
import { useFoInvestigatorAccess, useMe } from "@/lib/api";

/** A live verdict for one area. `label` exists because a dot has no text. */
export interface NavAreaStatus {
  tone: FreshnessTone;
  /** Announced to assistive tech, e.g. "Amber — 2 sites degraded". */
  label: string;
}

/** Per-area verdicts, keyed by `NavArea.id`. See the module comment. */
export type NavAreaStatusMap = Readonly<Record<string, NavAreaStatus>>;

/**
 * The live figures a sub-item may show beside its label, by the `liveCount`
 * kind the registry names. `null` means the API has not said — and then nothing
 * renders, never a `0`.
 */
type LiveCounts = Readonly<Record<NonNullable<NavSection["liveCount"]>, number | null>>;

interface SidebarNavProps {
  /** Per-area verdicts, if a caller ever has them. */
  areaStatus?: NavAreaStatusMap;
  /** Called after any link is followed — lets the drawer close itself. */
  onNavigate?: () => void;
}

/** Shared row geometry, so a link row and a disabled row line up exactly. */
const ROW = "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm";

/** Whether `pathname` is `path` or nested under it — a board's group opens on it. */
function isUnder(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

function AreaStatusDot({ status }: { status: NavAreaStatus | undefined }) {
  if (!status) {
    return null;
  }
  return <Freshness tone={status.tone} aria-label={status.label} />;
}

/** An area that has a page behind it: a real link. */
function AreaLink({
  area,
  to,
  active,
  status,
  onNavigate,
  hasBoards,
  expanded,
}: {
  area: NavArea;
  to: string;
  active: boolean;
  status: NavAreaStatus | undefined;
  onNavigate: (() => void) | undefined;
  hasBoards: boolean;
  expanded: boolean;
}) {
  return (
    <NavigationMenu.Link
      active={active}
      title={`${area.description} — ${area.systems}`}
      render={<Link to={to} />}
      onClick={onNavigate}
      // This row really does control the panel below it, so it says so. Only
      // when there is a panel: aria-expanded on a row with no children would
      // promise a disclosure that does not exist.
      aria-expanded={hasBoards ? expanded : undefined}
      className={cn(ROW, "font-medium")}
    >
      <Icon icon={area.icon} size="sm" />
      <span className="flex-1 truncate">{area.label}</span>
      <AreaStatusDot status={status} />
      {/* Own chevron, not a DS indicator: this row is a NavigationMenu.Link, not
          a Collapsible.Trigger, so there is nothing built in to reuse here. */}
      {hasBoards ? <DisclosureChevron expanded={expanded} /> : null}
    </NavigationMenu.Link>
  );
}

/**
 * The open/closed caret for an area row.
 *
 * `aria-hidden` on purpose: the row it sits in carries `aria-expanded` itself,
 * so announcing the caret too would say the same thing twice. The caret is the
 * visual half of that state, nothing more.
 */
function DisclosureChevron({ expanded }: { expanded: boolean }) {
  return (
    <Icon
      icon={ChevronDown}
      size="xs"
      aria-hidden
      className={cn("text-fg-muted transition-transform", expanded ? "rotate-0" : "-rotate-90")}
    />
  );
}

/**
 * An area with nothing behind it.
 *
 * A disabled `<button>`, not a greyed-out link: assistive tech announces it as
 * unavailable, and there is no href for a stray middle-click to open. The
 * system it is waiting on sits beside it in text — visible, and wired to the
 * control with `aria-describedby` — because the gap is the information.
 */
function AreaUnavailable({
  area,
  hasBoards,
  expanded,
  onToggle,
}: {
  area: NavArea;
  hasBoards: boolean;
  expanded: boolean;
  onToggle: (() => void) | undefined;
}) {
  const hintId = `nav-${area.id}-waiting-on`;

  return (
    <>
      {/* `disabled` only when there is genuinely nothing behind this row. An
          unavailable area that still carries shipped boards has to stay
          clickable, or its children — the only route to those boards — could
          never be revealed. The badge and the waiting-on line still say the
          rollup does not exist, so nothing is overclaimed by enabling it. */}
      <Button
        disabled={!hasBoards}
        fullWidth
        size="sm"
        variant="ghost"
        tone="neutral"
        aria-describedby={hintId}
        aria-expanded={hasBoards ? expanded : undefined}
        onClick={onToggle}
        startIcon={<Icon icon={area.icon} size="sm" />}
        className={cn(ROW, "h-auto justify-start")}
      >
        <span className="flex-1 truncate">{area.label}</span>
        {/* No badge. It read "Not yet available" at 9 px on a subtle amber pill —
            3.21:1 against its own background, which fails WCAG AA at that size —
            and its 17 characters were what pushed this rail 51 px past its own
            width, clipping the labels beside it. The `Waiting on …` line below
            says strictly more, at body size, and `aria-describedby` already ties
            it to this row, so nothing is lost by letting it carry the meaning
            alone. The muted row and the absent chevron do the at-a-glance work. */}
        {hasBoards ? <DisclosureChevron expanded={expanded} /> : null}
      </Button>
      <Typography as="p" id={hintId} variant="caption" tone="muted" className="px-3 pb-1 pl-9">
        Waiting on {area.waitingOn}
      </Typography>
    </>
  );
}

/** The board sub-item row geometry, one indent below an area. */
const BOARD_ROW = "py-1.5 text-xs";

/**
 * A board hanging under an area, plus its own screens when it has any.
 *
 * Three ways a row can render, and they are not interchangeable:
 *
 * - a **link**, the normal case;
 * - a **disabled row with one sentence**, when the reader is not on the F&O
 *   investigator list — the page would render and then refuse every payload, so
 *   the door is shown and explained rather than hidden (`./nav-access.ts`);
 * - **absent**, when an `AdminRoute` would bounce them (filtered out upstream).
 *
 * A board with children is its own disclosure, exactly as an area is: the row
 * navigates, the route changes, and being under it is what "open" means. So the
 * nested group needs no state of its own either.
 */
function BoardLink({
  board,
  activePath,
  pathname,
  viewer,
  liveCounts,
  onNavigate,
}: {
  board: NavSection;
  activePath: string | undefined;
  pathname: string;
  viewer: NavViewer;
  liveCounts: LiveCounts;
  onNavigate: (() => void) | undefined;
}) {
  const children = childBoards(board.path, viewer);
  const expanded = children.length > 0 && isUnder(pathname, board.path);

  return (
    <NavigationMenu.Item>
      <BoardRow
        board={board}
        active={board.path === activePath}
        viewer={viewer}
        liveCounts={liveCounts}
        onNavigate={onNavigate}
      />
      {children.length > 0 && (
        <Collapsible.Root open={expanded}>
          <Collapsible.Panel>
            <ul
              className={cn(
                "mt-0.5 mb-1 ml-4 flex list-none flex-col gap-0.5 border-l pl-2",
                expanded ? "border-accent" : "border-border",
              )}
            >
              {children.map((child) => (
                <NavigationMenu.Item key={child.path}>
                  <BoardRow
                    board={child}
                    active={child.path === activePath}
                    viewer={viewer}
                    liveCounts={liveCounts}
                    onNavigate={onNavigate}
                  />
                </NavigationMenu.Item>
              ))}
              {children.some((child) => boardVisibility(child, viewer) === "disabled") && (
                <li role="presentation" className="px-1 pt-0.5 pb-1">
                  <Typography variant="caption" tone="muted">
                    {INVESTIGATOR_ONLY_LINE}
                  </Typography>
                </li>
              )}
            </ul>
          </Collapsible.Panel>
        </Collapsible.Root>
      )}
    </NavigationMenu.Item>
  );
}

/** One board row: a link, or a disabled row when this reader's gate is shut. */
function BoardRow({
  board,
  active,
  viewer,
  liveCounts,
  onNavigate,
}: {
  board: NavSection;
  active: boolean;
  viewer: NavViewer;
  liveCounts: LiveCounts;
  onNavigate: (() => void) | undefined;
}) {
  const count = board.liveCount ? liveCounts[board.liveCount] : null;
  const title = count == null ? board.description : `${board.description} — ${count} open`;

  if (boardVisibility(board, viewer) === "disabled") {
    return (
      <Button
        disabled
        fullWidth
        size="sm"
        variant="ghost"
        tone="neutral"
        title={`${board.description} ${INVESTIGATOR_ONLY_LINE}`}
        startIcon={<Icon icon={board.icon} size="xs" />}
        className={cn(ROW, BOARD_ROW, "h-auto justify-start")}
      >
        <span className="flex-1 truncate">{board.label}</span>
        <Badge size="xs" tone="neutral" variant="outline">
          Investigators
        </Badge>
      </Button>
    );
  }

  return (
    <NavigationMenu.Link
      active={active}
      title={title}
      render={<Link to={board.path} />}
      onClick={onNavigate}
      className={cn(ROW, BOARD_ROW)}
    >
      <Icon icon={board.icon} size="xs" />
      <span className="flex-1 truncate">{board.label}</span>
      {/* Only ever rendered from a figure the API returned. An unknown count
          shows nothing at all — a nav that guessed a number here would be the
          same defect as the area status dots this file deliberately leaves
          empty. */}
      {count != null && (
        <Badge size="xs" tone={count > 0 ? "caution" : "neutral"} variant="subtle">
          {count}
        </Badge>
      )}
    </NavigationMenu.Link>
  );
}

function AreaRow({
  area,
  activePath,
  pathname,
  isCurrentArea,
  viewer,
  liveCounts,
  status,
  onNavigate,
  locallyExpanded,
  onToggleExpanded,
}: {
  area: NavArea;
  activePath: string | undefined;
  pathname: string;
  isCurrentArea: boolean;
  viewer: NavViewer;
  liveCounts: LiveCounts;
  status: NavAreaStatus | undefined;
  onNavigate: (() => void) | undefined;
  locallyExpanded: boolean;
  onToggleExpanded: () => void;
}) {
  const boards = boardsForArea(area, viewer);
  // `undefined` is the whole point: an area with no page behind it never gets
  // an href, so there is nothing for a stray click to open.
  const href = isAreaAvailable(area) ? area.path : undefined;
  const hasBoards = boards.length > 0;
  // An area with a route needs no state: clicking it navigates, which makes it
  // the current area, which is what "open" means. Only the routeless ones have
  // to remember anything.
  const expanded = hasBoards && (isCurrentArea || locallyExpanded);

  return (
    <NavigationMenu.Item className="w-full">
      {href === undefined ? (
        <AreaUnavailable
          area={area}
          hasBoards={hasBoards}
          expanded={expanded}
          onToggle={hasBoards ? onToggleExpanded : undefined}
        />
      ) : (
        <AreaLink
          area={area}
          to={href}
          active={href === activePath}
          status={status}
          onNavigate={onNavigate}
          hasBoards={hasBoards}
          expanded={expanded}
        />
      )}
      {boards.length > 0 && (
        // Controlled, with no Collapsible.Trigger at all: the area row above is
        // the control, so a trigger here would be a second one for the same
        // panel.
        <Collapsible.Root open={expanded}>
          {/* Neither hiddenUntilFound nor keepMounted, so the design system's
              own height animation runs: its panel rule transitions
              `height: var(--collapsible-panel-height)` and keys the endpoints off
              `[data-starting-style]` / `[data-ending-style]`, which Base UI only
              emits for a panel it mounts and unmounts. `hidden="until-found"`
              was here first for find-in-page, and it is what made the open jump
              instead of slide. The reachability it was guarding is now handled
              properly instead: an unavailable area that carries boards is a live
              button, so its children are one click away rather than one
              find-in-page away. */}
          <Collapsible.Panel>
            {/* Nested list, so the containment an indent implies visually is also
                there for a screen reader walking the nav. */}
            <ul
              className={cn(
                "mt-0.5 mb-1 ml-5 flex list-none flex-col gap-0.5 border-l pl-2",
                // The guide rail carries the containment: accent while the reader is
                // anywhere inside this area, so an area whose *sub-item* is the
                // current page still reads as the current area.
                isCurrentArea ? "border-accent" : "border-border",
              )}
            >
              {boards.map((board) => (
                <BoardLink
                  key={board.path}
                  board={board}
                  activePath={activePath}
                  pathname={pathname}
                  viewer={viewer}
                  liveCounts={liveCounts}
                  onNavigate={onNavigate}
                />
              ))}
            </ul>
          </Collapsible.Panel>
        </Collapsible.Root>
      )}
    </NavigationMenu.Item>
  );
}

/**
 * The areas with nothing behind them yet, behind one disclosure.
 *
 * They are real and they are coming, so they are not deleted — but a rail that
 * spends its top third on five things that do not exist buries the boards that
 * do. One row, a count, and the same explanations one click away.
 */
function DormantAreas({
  areas,
  expanded,
  onToggle,
}: {
  areas: readonly NavArea[];
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <NavigationMenu.Item className="w-full">
      <Button
        fullWidth
        size="sm"
        variant="ghost"
        tone="neutral"
        aria-expanded={expanded}
        onClick={onToggle}
        startIcon={<Icon icon={Clock} size="sm" />}
        className={cn(ROW, "h-auto justify-start")}
      >
        <span className="flex-1 truncate text-left">Not yet connected</span>
        <Badge size="xs" tone="neutral" variant="outline">
          {areas.length}
        </Badge>
        <DisclosureChevron expanded={expanded} />
      </Button>
      <Collapsible.Root open={expanded}>
        <Collapsible.Panel>
          <ul className="border-border mt-0.5 mb-1 ml-5 flex list-none flex-col gap-0.5 border-l pl-2">
            {areas.map((area) => (
              <li key={area.id} className="w-full">
                <div className="text-fg-muted flex items-center gap-2 px-3 py-1.5 text-xs">
                  <Icon icon={area.icon} size="xs" aria-hidden="true" />
                  <span className="truncate">{area.label}</span>
                </div>
                <Typography as="p" variant="caption" tone="muted" className="px-3 pb-1 pl-7">
                  Waiting on {area.waitingOn}
                </Typography>
              </li>
            ))}
          </ul>
        </Collapsible.Panel>
      </Collapsible.Root>
    </NavigationMenu.Item>
  );
}

function GroupHeading({ group, withRule }: { group: NavAreaGroup; withRule: boolean }) {
  return (
    // Presentational: a heading is not one of the navigable items.
    <li role="presentation" className="px-3 pt-2 pb-1">
      {withRule && <Separator className="mb-3" />}
      <Typography as="h2" variant="caption" tone="muted" className="tracking-wide uppercase">
        {NAV_AREA_GROUP_LABEL[group]}
      </Typography>
    </li>
  );
}

/**
 * The nav itself, with no scroll container of its own.
 *
 * The two hosts scroll differently — the desktop rail wraps this in a
 * `ScrollArea`, while `Drawer.Body` already scrolls — so the scrolling belongs
 * to the host, not here.
 */
export function AppSidebarNav({ areaStatus, onNavigate }: SidebarNavProps) {
  const { pathname } = useLocation();
  const { data: me } = useMe();
  // The investigator half of the viewer is asked of the API, never assumed
  // from a mail: `useFoInvestigatorAccess` reads the one gated route that needs
  // no reason and writes no audit row, and reports `denied` on its 403. It
  // shares its query with the report queue and the overview tile, so on the
  // boards that already read the queue this is not a second request.
  const investigator = useFoInvestigatorAccess();
  const viewer: NavViewer = {
    isAdmin: me?.role === "admin",
    investigator: investigator.access,
  };
  const liveCounts: LiveCounts = { "fo-open-reports": investigator.openReports };
  const activePath = navPathForPath(pathname);
  const currentArea = areaForPath(pathname);
  // Only the areas with no route of their own need remembering — the rest take
  // their open state from the URL. Held here rather than per row so it survives
  // navigation, which it must: the shell stays mounted across route changes.
  const [expandedUnbuilt, setExpandedUnbuilt] = useState<ReadonlySet<string>>(new Set());
  const toggleUnbuilt = (id: string) =>
    setExpandedUnbuilt((current) => {
      const next = new Set(current);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });

  return (
    <NavigationMenu.Root
      orientation="vertical"
      aria-label="NOVA navigation"
      className="w-full flex-col items-stretch"
    >
      <NavigationMenu.List className="w-full list-none flex-col items-stretch gap-0.5 p-2">
        {NAV_AREA_GROUPS.map((group, groupIndex) => {
          // An area that is unavailable AND carries no boards has nothing behind
          // it at all, so it is filed under one disclosure at the end of its
          // group rather than given a permanent row of its own. Five of them,
          // each with a two-line explanation, took roughly 40 % of this rail and
          // pushed the boards that do work below the fold. Nothing is hidden:
          // the group is one click, its label counts what is inside, and each
          // row keeps its own "waiting on" line.
          //
          // An unavailable area that DOES carry boards stays exactly where it
          // is. Its children are the only route to those boards, so folding it
          // away would make them unreachable.
          const areas = areasInGroup(group);
          const dormant = areas.filter(
            (area) => !isAreaAvailable(area) && boardsForArea(area, viewer).length === 0,
          );
          const live = areas.filter((area) => !dormant.includes(area));
          const row = (area: NavArea) => (
            <AreaRow
              key={area.id}
              area={area}
              activePath={activePath}
              pathname={pathname}
              isCurrentArea={currentArea?.id === area.id}
              viewer={viewer}
              liveCounts={liveCounts}
              status={areaStatus?.[area.id]}
              onNavigate={onNavigate}
              locallyExpanded={expandedUnbuilt.has(area.id)}
              onToggleExpanded={() => toggleUnbuilt(area.id)}
            />
          );
          return (
            <Fragment key={group}>
              <GroupHeading group={group} withRule={groupIndex > 0} />
              {live.map(row)}
              {dormant.length > 0 ? (
                <DormantAreas
                  areas={dormant}
                  expanded={expandedUnbuilt.has(`dormant-${group}`)}
                  onToggle={() => toggleUnbuilt(`dormant-${group}`)}
                />
              ) : null}
            </Fragment>
          );
        })}
      </NavigationMenu.List>
    </NavigationMenu.Root>
  );
}

/**
 * The always-on desktop rail.
 *
 * Sticky under the shell header rather than inside a scroll container of its
 * own: the boards below assume the window scrolls (several pin their own
 * headers to `top-0`), so the shell must not take that away. `top-14` and the
 * `100dvh - 3.5rem` height are the shell header's `h-14` — the three move
 * together.
 *
 * The `sticky` sits on the `aside`, not on the `ScrollArea` inside it. A sticky
 * element can only travel within its containing block, and the aside is exactly
 * one viewport tall — so a sticky child of the aside would have nowhere to go
 * and the rail would scroll away with the page. As a sticky flex item its
 * containing block is the shell's row, which is as tall as the board.
 *
 * The z-index keeps it above the boards' own sticky DataGrid headers
 * (`--norican-z-sticky`) and below the shell header, which takes `+10`.
 */
export function AppSidebar({ areaStatus }: { areaStatus?: NavAreaStatusMap }) {
  return (
    <aside className="bg-bg border-border sticky top-14 z-[calc(var(--norican-z-sticky)+5)] hidden h-[calc(100dvh-3.5rem)] w-64 shrink-0 border-r lg:block">
      <ScrollArea.Root className="h-full">
        <ScrollArea.Viewport>
          <ScrollArea.Content>
            <AppSidebarNav areaStatus={areaStatus} />
          </ScrollArea.Content>
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar orientation="vertical">
          <ScrollArea.Thumb />
        </ScrollArea.Scrollbar>
      </ScrollArea.Root>
    </aside>
  );
}

/**
 * The same nav as a left-edge drawer, for viewports too narrow for the rail.
 *
 * A drawer rather than a squeezed column: below `lg` a 16rem rail would eat the
 * content it exists to navigate. `Drawer` is controlled here so following a
 * link can close it — `NavigationMenu.Link`'s own `closeOnClick` dismisses a
 * navigation-menu popup, which is not the same thing as this drawer.
 */
export function AppSidebarDrawer({ areaStatus }: { areaStatus?: NavAreaStatusMap }) {
  const [open, setOpen] = useState(false);

  return (
    <Drawer.Root side="left" open={open} onOpenChange={setOpen}>
      <Drawer.Trigger
        render={
          <Button
            iconOnly
            variant="ghost"
            tone="neutral"
            aria-label="Open navigation"
            className="lg:hidden"
          >
            <Icon icon={PanelLeft} size="sm" />
          </Button>
        }
      />
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Viewport>
          <Drawer.Popup>
            <Drawer.Header>
              <Drawer.Title>Navigation</Drawer.Title>
              <Drawer.CloseButton label="Close navigation" />
            </Drawer.Header>
            <Drawer.Body>
              <AppSidebarNav areaStatus={areaStatus} onNavigate={() => setOpen(false)} />
            </Drawer.Body>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
